import type { Class, Sample } from '../data/types';
import type { DatasetObservation } from './datasetObservations';

/**
 * Bias-awareness observation: when nearly every sample in a class contains
 * a face, the model may be learning *who is in the picture* instead of the
 * thing the kid is trying to teach. Phrased as an invitation, not a
 * scolding — see CLAUDE.md "Honesty about the model's limits should never
 * feel like a scolding."
 *
 * Two pieces here:
 * - `observeFaceBias`: pure, synchronous. Takes classes + samples + a
 *   per-sample face count and returns the observation list. Easy to test.
 * - `detectFaceCount`: async, browser-only. Lazy-loads BlazeFace from
 *   `@tensorflow-models/face-detection` and runs it on the sample blob.
 *   The hook in `useFaceBias` calls this for samples we haven't seen.
 */

/** Surface the warning only when at least this fraction of samples in a
 *  class contain a face. 0.8 is high enough that one or two stray frames
 *  don't trigger it on a "wave at the camera" class where the kid happens
 *  to bend out of frame. */
const FACE_BIAS_THRESHOLD = 0.8;
/** Don't bother nudging until the class has a meaningful sample count.
 *  A 3-sample class with 3 faces is a coincidence, not a pattern. */
const FACE_BIAS_MIN_SAMPLES = 8;

export function observeFaceBias(
  classes: Pick<Class, 'id' | 'name'>[],
  samples: Pick<Sample, 'id' | 'classId'>[],
  faceCounts: ReadonlyMap<string, number>,
): DatasetObservation[] {
  if (classes.length < 2) return [];

  const observations: DatasetObservation[] = [];
  const samplesByClass = new Map<string, string[]>();
  for (const s of samples) {
    const list = samplesByClass.get(s.classId) ?? [];
    list.push(s.id);
    samplesByClass.set(s.classId, list);
  }

  for (const cls of classes) {
    const ids = samplesByClass.get(cls.id) ?? [];
    if (ids.length < FACE_BIAS_MIN_SAMPLES) continue;
    // Only count samples we have a result for. Pending detections don't
    // change the class score in either direction.
    let scored = 0;
    let withFaces = 0;
    for (const id of ids) {
      const count = faceCounts.get(id);
      if (count === undefined) continue;
      scored += 1;
      if (count >= 1) withFaces += 1;
    }
    if (scored < FACE_BIAS_MIN_SAMPLES) continue;
    if (withFaces / scored < FACE_BIAS_THRESHOLD) continue;

    observations.push({
      kind: 'face-bias',
      message: `Almost every picture in ${cls.name} has a face. Make sure the model is learning the thing you want it to learn, not just who is in front of the camera. Try pictures with different people, or no person at all.`,
      classId: cls.id,
    });
  }

  return observations;
}

/**
 * Detector handle. Lazy-loaded the first time we need it. We keep one
 * instance per page — the kid only ever has one project open and the
 * detector is stateless across calls.
 */
type FaceDetector = {
  estimateFaces: (input: ImageBitmap | HTMLImageElement | HTMLCanvasElement) => Promise<unknown[]>;
};
let detectorPromise: Promise<FaceDetector> | null = null;

async function getDetector(): Promise<FaceDetector> {
  if (!detectorPromise) {
    detectorPromise = (async () => {
      const fd = await import('@tensorflow-models/face-detection');
      const model = fd.SupportedModels.MediaPipeFaceDetector;
      // The `tfjs` runtime keeps us inside the in-browser TF.js stack we
      // already ship — no MediaPipe wasm fetched from a CDN.
      return (await fd.createDetector(model, {
        runtime: 'tfjs',
        modelType: 'short',
      })) as unknown as FaceDetector;
    })().catch((err) => {
      // Reset so a future call can retry rather than wedging on a stale
      // rejected promise (e.g. the kid was offline on first load).
      detectorPromise = null;
      throw err;
    });
  }
  return detectorPromise;
}

export async function detectFaceCount(blob: Blob): Promise<number> {
  const bitmap = await createImageBitmap(blob);
  try {
    const detector = await getDetector();
    const faces = await detector.estimateFaces(bitmap);
    return faces.length;
  } finally {
    bitmap.close();
  }
}

/** Test-only escape hatch — lets the hook unit test stub detection without
 *  pulling in the real TF.js model. */
export const __test = {
  resetDetector(): void {
    detectorPromise = null;
  },
};
